import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { X, Barcode, CheckCircle } from '@phosphor-icons/react';
import { useBarcode } from '../contexts/BarcodeScannerContext';
import { toast } from 'sonner';

const BarcodeScanner = () => {
  const { isOpen, closeScanner, handleScan } = useBarcode();
  const [isScanning, setIsScanning] = useState(false);
  const [lastScanned, setLastScanned] = useState(null);
  const [scanCount, setScanCount] = useState(0);
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);

  useEffect(() => {
    if (isOpen && !isScanning) {
      startScanner();
    }

    return () => {
      stopScanner();
    };
  }, [isOpen]);

  const startScanner = async () => {
    try {
      console.log('🎥 Starting barcode scanner...');
      
      // Skip the pre-check and let html5-qrcode handle permissions directly
      if (html5QrCodeRef.current) {
        console.log('⚠️ Scanner already exists, cleaning up first...');
        await stopScanner();
      }
      
      // CRITICAL FIX: Wait for DOM element to be mounted before initializing scanner
      console.log('⏳ Waiting for #barcode-reader element to be mounted...');
      let retries = 0;
      const maxRetries = 20;
      
      while (retries < maxRetries) {
        const element = document.getElementById('barcode-reader');
        if (element) {
          console.log('✅ DOM element found after', retries, 'retries');
          break;
        }
        await new Promise(resolve => setTimeout(resolve, 50));
        retries++;
      }
      
      if (!document.getElementById('barcode-reader')) {
        throw new Error('Timeout: #barcode-reader element not found after 1 second');
      }
      
      console.log('📱 Initializing Html5Qrcode instance...');
      html5QrCodeRef.current = new Html5Qrcode('barcode-reader');

      const qrCodeScanner = html5QrCodeRef.current;
      console.log('🔧 Scanner instance ready, starting camera...');

      await qrCodeScanner.start(
        'environment', // Simplified: just pass 'environment' as string
        {
          fps: 10,
          qrbox: function(viewfinderWidth, viewfinderHeight) {
            const isMobile = window.innerWidth < 768;
            if (isMobile) {
              const boxWidth = Math.floor(viewfinderWidth * 0.85);
              const boxHeight = Math.floor(viewfinderHeight * 0.35);
              console.log(`📐 Mobile qrbox: ${boxWidth}x${boxHeight}`);
              return { width: boxWidth, height: boxHeight };
            }
            console.log('📐 Desktop qrbox: 350x180');
            return { width: 350, height: 180 };
          },
          aspectRatio: 1.6,
          formatsToSupport: [
            0,  // QR_CODE
            1,  // AZTEC
            2,  // CODABAR
            3,  // CODE_39
            4,  // CODE_93
            5,  // CODE_128
            6,  // DATA_MATRIX
            7,  // MAXICODE
            8,  // ITF
            9,  // EAN_13
            10, // EAN_8
            11, // PDF_417
            12, // RSS_14
            13, // RSS_EXPANDED
            14, // UPC_A
            15, // UPC_E
            16, // UPC_EAN_EXTENSION
          ],
          disableFlip: false,
          verbose: true,
          experimentalFeatures: {
            useBarCodeDetectorIfSupported: true
          }
        },
        (decodedText) => {
          console.log('✅ Barcode scanned successfully:', decodedText);
          setLastScanned(decodedText);
          setScanCount(prev => prev + 1);
          handleScan(decodedText);
          toast.success(`Scanned: ${decodedText}`, {
            duration: 2000,
            icon: '✅'
          });
          
          const readerElement = document.getElementById('barcode-reader');
          if (readerElement) {
            readerElement.style.border = '3px solid #22c55e';
            setTimeout(() => {
              readerElement.style.border = '2px solid #e5e7eb';
            }, 500);
          }
        },
        (errorMessage) => {
          // Scanning in progress - not a real error
        }
      );

      console.log('✅ Scanner started successfully');
      setIsScanning(true);
    } catch (err) {
      console.error('❌ Scanner startup failed:', err);
      console.error('Error name:', err.name);
      console.error('Error message:', err.message);
      console.error('Full error:', JSON.stringify(err, null, 2));
      
      let userMessage = 'Failed to start camera. ';
      
      if (err.name === 'NotAllowedError') {
        userMessage += 'Please allow camera access when prompted, or check your browser settings.';
      } else if (err.name === 'NotFoundError') {
        userMessage += 'No camera found on your device.';
      } else if (err.name === 'NotReadableError') {
        userMessage += 'Camera is already in use by another app. Please close other apps and try again.';
      } else if (err.name === 'OverconstrainedError') {
        userMessage += 'Your camera does not support the required settings. Please try a different device.';
      } else if (err.message) {
        userMessage += `Error: ${err.message}`;
      } else {
        userMessage += 'Please check browser console for details.';
      }
      
      toast.error(userMessage, { duration: 7000 });
      closeScanner();
    }
  };

  const stopScanner = async () => {
    try {
      if (html5QrCodeRef.current) {
        console.log('🛑 Stopping scanner...');
        
        // Stop camera stream if scanning
        if (html5QrCodeRef.current.isScanning) {
          await html5QrCodeRef.current.stop();
          console.log('📹 Camera stopped');
        }
        
        // CRITICAL: Must call clear() to release DOM ownership
        await html5QrCodeRef.current.clear();
        console.log('🧹 Scanner cleared');
        
        html5QrCodeRef.current = null;
      }
      setIsScanning(false);
      setLastScanned(null);
      setScanCount(0);
    } catch (err) {
      console.error('❌ Error stopping scanner:', err);
      // Force cleanup even if error occurs
      html5QrCodeRef.current = null;
    }
  };

  const handleClose = async () => {
    await stopScanner();
    closeScanner();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-white w-full h-full max-w-full md:max-w-4xl md:h-auto md:max-h-[90vh] p-4 md:p-6 overflow-y-auto" data-testid="barcode-scanner-dialog">
        <DialogHeader>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Barcode size={28} className="text-[#2C4C3B]" weight="duotone" />
              <DialogTitle className="text-xl md:text-2xl font-semibold text-[#2C4C3B]">
                Barcode Scanner
              </DialogTitle>
            </div>
            <Button
              variant="ghost"
              size="lg"
              onClick={handleClose}
              className="h-11 w-11 md:h-8 md:w-8 p-0 rounded-full flex-shrink-0"
              data-testid="close-scanner-btn"
            >
              <X size={24} className="md:w-5 md:h-5" />
            </Button>
          </div>
        </DialogHeader>

        <div className="space-y-3 md:space-y-4 pb-4">
          {/* Scanner Status */}
          <div className="flex items-center justify-between p-3 md:p-3 bg-green-50 border border-green-200 rounded-lg flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 md:w-2 md:h-2 bg-green-500 rounded-full animate-pulse flex-shrink-0" />
              <span className="text-sm md:text-sm font-medium text-green-800" data-testid="camera-status">
                Camera Active - Ready to Scan
              </span>
            </div>
            <Badge variant="outline" className="bg-white text-base md:text-sm px-3 py-1 md:px-2 md:py-0" data-testid="scan-count-badge">
              {scanCount} scanned
            </Badge>
          </div>

          {/* Scanner View */}
          <div className="relative">
            <div
              id="barcode-reader"
              ref={scannerRef}
              data-testid="barcode-reader"
              className="w-full rounded-lg overflow-hidden border-2 border-gray-200"
              style={{ minHeight: '350px', maxHeight: '400px' }}
            />
            
            {/* Scanning Overlay */}
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none">
              <div className="w-[280px] h-[140px] md:w-72 md:h-36 border-2 md:border-2 border-green-500 rounded-lg relative">
                <div className="absolute top-0 left-0 w-6 h-6 md:w-4 md:h-4 border-t-4 border-l-4 border-green-500" />
                <div className="absolute top-0 right-0 w-6 h-6 md:w-4 md:h-4 border-t-4 border-r-4 border-green-500" />
                <div className="absolute bottom-0 left-0 w-6 h-6 md:w-4 md:h-4 border-b-4 border-l-4 border-green-500" />
                <div className="absolute bottom-0 right-0 w-6 h-6 md:w-4 md:h-4 border-b-4 border-r-4 border-green-500" />
              </div>
            </div>
          </div>

          {/* Last Scanned */}
          {lastScanned && (
            <div className="p-4 md:p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
              <CheckCircle size={24} className="md:w-5 md:h-5 text-green-600" weight="fill" />
              <div className="flex-1">
                <p className="text-sm md:text-xs text-green-700 font-medium">Last Scanned:</p>
                <p className="text-base md:text-sm font-mono text-green-900">{lastScanned}</p>
              </div>
            </div>
          )}

          {/* Instructions */}
          <div className="p-4 md:p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm md:text-xs text-blue-800 mb-2">
              <strong>📱 Mobile/Tablet:</strong> Hold device steady and position barcode within the green frame. Camera will auto-scan.
            </p>
            <p className="text-sm md:text-xs text-blue-700">
              <strong>Camera not working?</strong> Check browser permissions: 🔒 icon in address bar → Site Settings → Camera → Allow.
            </p>
          </div>

          {/* Close Button */}
          <Button
            onClick={handleClose}
            variant="outline"
            className="w-full h-12 md:h-10 text-base md:text-sm font-medium"
          >
            Close Scanner
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BarcodeScanner;
